import { useEffect, useRef, useState } from 'react';
import { getActivePlan, type ActivePlan } from '../data/planSchedule';
import { useDetailedSimulation } from '../store/detailedSimulation';

/**
 * Tracks the active plan based on the real clock.
 * Updates every 15s and triggers store.setPlan() on plan changes.
 * Returns the current time string and active plan info.
 */
export function usePlanSchedule() {
  const setPlan = useDetailedSimulation(s => s.setPlan);
  const [now, setNow] = useState(() => new Date());
  const [activePlan, setActivePlan] = useState<ActivePlan>(() => getActivePlan(new Date()));
  const lastPlanRef = useRef(activePlan.config.plan);

  useEffect(() => {
    // Initial sync
    const initial = getActivePlan(new Date());
    setPlan(initial.config.plan, initial.config.cycleTime, initial.config.phaseDurations);
    lastPlanRef.current = initial.config.plan;

    const timer = setInterval(() => {
      const date = new Date();
      setNow(date);
      const plan = getActivePlan(date);
      setActivePlan(plan);

      if (plan.config.plan !== lastPlanRef.current) {
        setPlan(plan.config.plan, plan.config.cycleTime, plan.config.phaseDurations);
        lastPlanRef.current = plan.config.plan;
      }
    }, 15_000); // check every 15s

    return () => clearInterval(timer);
  }, [setPlan]);

  const clock = now.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });

  return { clock, now, activePlan };
}
